import { UserIcon } from "@heroicons/react/outline";
import { ICellRendererParams } from "ag-grid-community";
import { useMemo } from "react";

import { isOrganizationMember, isWorkspaceInvite, NormalizedMemberData } from "./utils";

import { Badge, BadgeVariant } from "@/components/ui/Badge";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { OrganizationMemberLifecycleStatus } from "@/enums/organization-member-lifecycle-status";
import { formatTimestamp } from "@/utils/timestamp";
import { useUser } from "@/context/user-context";
import { capitalize } from "@/utils/strings";

export const useOrganizationMembersColumns = () => {
  const userContext = useUser();
  const columns = useMemo(
    () => [
      {
        id: 1,
        name: "user",
        header: "User",
        sortable: false,
        minWidth: 300,
        cellRenderer: (params: ICellRendererParams<NormalizedMemberData>) => {
          const member = params.data;
          if (!member) {
            return "";
          }
          const email = isOrganizationMember(member) ? member.user.email : member.invitee_email;
          const name = isOrganizationMember(member) ? member.user.name : null;
          const isCurrentUser =
            isOrganizationMember(member) && member.user.id === userContext?.user?.id;

          return (
            <div className="flex w-full items-center space-x-3">
              <div className="flex max-h-8 min-h-8 min-w-8 max-w-8 flex-shrink-0 items-center justify-center rounded-full border border-gray-200 bg-gray-100">
                <UserIcon className="h-4 w-4 text-gray-600" />
              </div>
              <div className="flex min-w-0 flex-1 flex-col">
                <OverflowDisplayTooltip content={email} shouldShow={Boolean(email)}>
                  <span className="text-sm font-medium text-gray-900">
                    {email} {isCurrentUser && "(You)"}
                  </span>
                </OverflowDisplayTooltip>
                {name && <span className="truncate text-sm text-gray-500">{name}</span>}
              </div>
            </div>
          );
        },
      },
      {
        id: 2,
        name: "role",
        header: "Organization Role",
        sortable: false,
        cellRenderer: (params: ICellRendererParams<NormalizedMemberData>) => {
          const member = params.data;
          if (!member) {
            return "";
          }
          if (isWorkspaceInvite(member)) {
            return (
              <Badge variant={BadgeVariant.GRAY} className="capitalize">
                User
              </Badge>
            );
          }
          return (
            <Badge
              variant={
                member.organization_role.name === "user" ? BadgeVariant.GRAY : BadgeVariant.BLUE
              }
              className="capitalize"
            >
              {capitalize(member.organization_role.name)}
            </Badge>
          );
        },
      },
      {
        id: 3,
        name: "workspaces",
        header: "Workspaces",
        sortable: false,
        cellRenderer: (params: ICellRendererParams<NormalizedMemberData>) => {
          const member = params.data;
          if (!member) {
            return "";
          }

          const workspacesText = isWorkspaceInvite(member)
            ? member.invites
                .map(i => i.workspace?.name)
                .filter(Boolean)
                .join(", ") || "Unknown"
            : member.workspaces.map(workspace => workspace.name).join(", ");

          return (
            <OverflowDisplayTooltip content={workspacesText} shouldShow={Boolean(workspacesText)}>
              <span className="text-sm text-gray-900">{workspacesText}</span>
            </OverflowDisplayTooltip>
          );
        },
      },
      {
        id: 4,
        name: "status",
        header: "Status",
        sortable: false,
        cellRenderer: (params: ICellRendererParams<NormalizedMemberData>) => {
          const member = params.data;
          if (!member) {
            return "";
          }

          if (isWorkspaceInvite(member)) {
            const allExpired = member.invites.every(
              i => i.is_expired || new Date(i.expires_at) < new Date(),
            );

            return (
              <Badge
                variant={allExpired ? BadgeVariant.RED : BadgeVariant.ORANGE}
                className="capitalize"
              >
                {allExpired ? "Expired" : "Pending invite"}
              </Badge>
            );
          }

          switch (member.status) {
            case OrganizationMemberLifecycleStatus.DEACTIVATED:
              return (
                <BasicTooltip
                  content={
                    <div className="text-xs">
                      {member.deactivated_at
                        ? `Deprovisioned ${formatTimestamp(member.deactivated_at)}`
                        : "Removed via directory sync or SCIM deactivation"}
                    </div>
                  }
                >
                  <Badge variant={BadgeVariant.RED}>Deactivated</Badge>
                </BasicTooltip>
              );
            case OrganizationMemberLifecycleStatus.PROVISIONED:
              return (
                <BasicTooltip
                  content={
                    <div className="text-xs">
                      Provisioned from your identity provider; awaiting first dashboard sign-in
                    </div>
                  }
                >
                  <Badge variant={BadgeVariant.ORANGE}>Awaiting sign-in</Badge>
                </BasicTooltip>
              );
            case OrganizationMemberLifecycleStatus.ACTIVE:
            default: {
              const lastActive = member.user.last_active;

              return (
                <BasicTooltip
                  content={
                    <div className="text-xs">
                      {lastActive
                        ? `Last active: ${formatTimestamp(lastActive)}`
                        : "Last activity unknown"}
                    </div>
                  }
                >
                  <Badge variant={BadgeVariant.GREEN} className="capitalize">
                    Active
                  </Badge>
                </BasicTooltip>
              );
            }
          }
        },
      },
      {
        id: 5,
        name: "joined",
        header: "Joined",
        // eslint-disable-next-line max-params
        comparator: (
          valueA: string | undefined,
          valueB: string | undefined,
          member: ICellRendererParams<NormalizedMemberData>,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          _: any,
          isDescending: boolean,
        ) => {
          if (!valueA || !valueB) {
            return 0;
          }

          if (isDescending && isWorkspaceInvite(member.data!)) {
            return 1;
          }

          if (isWorkspaceInvite(member.data!)) {
            return 0;
          }

          return new Date(valueA).getTime() - new Date(valueB).getTime();
        },
        field: "created_at",
        cellRenderer: (params: ICellRendererParams<NormalizedMemberData>) => {
          const member = params.data;
          if (!member) {
            return <div className="text-sm text-gray-600">-</div>;
          }

          if (isWorkspaceInvite(member)) {
            return <div className="text-sm text-gray-600">-</div>;
          }

          const createdAt = member.created_at;
          if (!createdAt) {
            return <div className="text-sm text-gray-600">-</div>;
          }

          return <div className="text-sm text-gray-600">{formatTimestamp(createdAt)}</div>;
        },
      },
      {
        id: 6,
        name: "last_active",
        header: "Last Active",
        comparator: (valueA: string | undefined, valueB: string | undefined) => {
          if (!valueA || !valueB) {
            return 0;
          }
          return new Date(valueA).getTime() - new Date(valueB).getTime();
        },
        field: "user.last_active",
        cellRenderer: (params: ICellRendererParams<NormalizedMemberData>) => {
          const member = params.data;
          if (!member) {
            return <div className="text-sm text-gray-600">-</div>;
          }

          if (isOrganizationMember(member) && member.status === "provisioned") {
            return <div className="text-sm text-gray-600">-</div>;
          }

          if (isOrganizationMember(member) && member.user.last_active) {
            return (
              <div className="text-sm text-gray-600">
                {formatTimestamp(member.user.last_active)}
              </div>
            );
          }

          return <div className="text-sm text-gray-600">-</div>;
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return { columns };
};
